import { useEffect, useState } from "react";
import { useParams, Link, Navigate } from "@/lib/router-compat";
import { useLoaderData } from "@tanstack/react-router";
import { supabase } from "@/integrations/supabase/client";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { format } from "date-fns";
import { ArrowLeft } from "lucide-react";
import DOMPurify from "dompurify";
import PostFaq from "@/components/PostFaq";
import { POST_FAQS } from "@/data/postFaqs";

// DOMPurify needs a DOM, so it is a no-op during SSR. Blog HTML comes from the
// admin-only CMS and is sanitized again in the browser on hydration.
const sanitizePostHtml = (html: string) =>
  typeof window === "undefined" || typeof DOMPurify.sanitize !== "function"
    ? html
    : DOMPurify.sanitize(html, { USE_PROFILES: { html: true } });

interface BlogPost {
  id: string;
  title: string;
  slug: string;
  content: string | null;
  excerpt: string | null;
  featured_image_url: string | null;
  published_at: string | null;
  updated_at: string;
  seo_title: string | null;
  seo_description: string | null;
}

interface RelatedPost {
  id: string;
  title: string;
  slug: string;
  excerpt: string | null;
  featured_image_url: string | null;
}

// Targeted 301 redirects for retired posts → most relevant live post
const RETIRED_POST_REDIRECTS: Record<string, string> = {
  "the-future-of-eyewear-a-closer-look-at-customizable-eyewear":
    "/blog/building-prescription-eyewear-catalog-without-inventory",
  "exploring-the-latest-trends-in-digital-eyewear-and-fashion":
    "/blog/ai-optical-retail-conversational-ai",
  "fashion-tech-in-the-eyewear-industry":
    "/blog/why-most-vto-tools-disappoint",
  "the-evolution-of-eyewear-fashion-from-necessity-to-style-statement":
    "/blog",
  "online-evolution-in-the-eyewear-industry":
    "/blog/how-to-start-online-eyewear-business-2026",
  "the-future-of-b2b-marketplaces-in-the-eyewear-industry":
    "/blog/optical-lab-networks-backbone-online-eyewear-fulfillment",
  "what-eyewear-consumers-are-looking-for":
    "/blog/five-biggest-mistakes-online-rx-eyewear",
  "engraving-your-vision-the-art-of-personalizing-eyewear":
    "/blog",
  "how-important-is-personalized-eyewear":
    "/blog",
};

// Contextual link from each blog post → most relevant solution page
const POST_TO_SOLUTION: Record<string, { href: string; label: string; intro: string }> = {
  "pharmacies-selling-prescription-eyewear": {
    href: "/pharmacy",
    label: "GoHub for online pharmacies",
    intro: "Want the full picture for your pharmacy?",
  },
  "optical-lab-networks-backbone-online-eyewear-fulfillment": {
    href: "/brands-resellers",
    label: "GoHub for eyewear brands and resellers",
    intro: "See how brands plug into our 12-lab fulfillment network:",
  },
  "building-prescription-eyewear-catalog-without-inventory": {
    href: "/brands-resellers",
    label: "GoHub for eyewear brands and resellers",
    intro: "Launch a 17,000-frame catalog with zero inventory:",
  },
  "five-biggest-mistakes-online-rx-eyewear": {
    href: "/virtual-try-on",
    label: "GoHub Virtual Try-On & PD measurement",
    intro: "Fix the biggest UX leaks with the right tooling:",
  },
  "why-most-vto-tools-disappoint": {
    href: "/virtual-try-on",
    label: "GoHub Virtual Try-On & PD measurement",
    intro: "See what real AR virtual try-on looks like:",
  },
  "sell-rx-eyewear-without-being-optician": {
    href: "/pharmacy",
    label: "GoHub for online pharmacies",
    intro: "No optical expertise? Start here:",
  },
  "ai-optical-retail-conversational-ai": {
    href: "/virtual-try-on",
    label: "GoHub Virtual Try-On & PD measurement",
    intro: "Pair AI guidance with virtual try-on:",
  },
  "pupillary-distance-measurement-online": {
    href: "/virtual-try-on",
    label: "GoHub Virtual Try-On & PD measurement",
    intro: "PD measurement is built into our virtual try-on:",
  },
  "how-to-start-online-eyewear-business-2026": {
    href: "/brands-resellers",
    label: "GoHub for eyewear brands and resellers",
    intro: "Ready to launch the full eyewear stack as one system?",
  },
  "how-online-pharmacy-sell-prescription-glasses": {
    href: "/pharmacy",
    label: "GoHub for online pharmacies",
    intro: "Ready to add prescription eyewear to your pharmacy?",
  },
  "sell-prescription-glasses-online-without-inventory": {
    href: "/opticians",
    label: "GoHub for opticians and online retailers",
    intro: "Launch a prescription eyewear store with zero inventory:",
  },
};

// Curated thematically-related posts per slug (3 each). Falls back to most-recent.
const RELATED_POSTS: Record<string, string[]> = {
  "pupillary-distance-measurement-online": [
    "ai-optical-retail-conversational-ai",
    "why-most-vto-tools-disappoint",
    "five-biggest-mistakes-online-rx-eyewear",
  ],
  "sell-rx-eyewear-without-being-optician": [
    "ai-optical-retail-conversational-ai",
    "pharmacies-selling-prescription-eyewear",
    "building-prescription-eyewear-catalog-without-inventory",
  ],
  "five-biggest-mistakes-online-rx-eyewear": [
    "why-most-vto-tools-disappoint",
    "pupillary-distance-measurement-online",
    "ai-optical-retail-conversational-ai",
  ],
  "why-most-vto-tools-disappoint": [
    "pupillary-distance-measurement-online",
    "five-biggest-mistakes-online-rx-eyewear",
    "ai-optical-retail-conversational-ai",
  ],
  "ai-optical-retail-conversational-ai": [
    "why-most-vto-tools-disappoint",
    "pupillary-distance-measurement-online",
    "sell-rx-eyewear-without-being-optician",
  ],
  "pharmacies-selling-prescription-eyewear": [
    "how-online-pharmacy-sell-prescription-glasses",
    "sell-rx-eyewear-without-being-optician",
    "building-prescription-eyewear-catalog-without-inventory",
  ],
  "how-online-pharmacy-sell-prescription-glasses": [
    "pharmacies-selling-prescription-eyewear",
    "sell-rx-eyewear-without-being-optician",
    "building-prescription-eyewear-catalog-without-inventory",
  ],
  "optical-lab-networks-backbone-online-eyewear-fulfillment": [
    "building-prescription-eyewear-catalog-without-inventory",
    "how-to-start-online-eyewear-business-2026",
    "five-biggest-mistakes-online-rx-eyewear",
  ],
  "building-prescription-eyewear-catalog-without-inventory": [
    "sell-prescription-glasses-online-without-inventory",
    "optical-lab-networks-backbone-online-eyewear-fulfillment",
    "how-to-start-online-eyewear-business-2026",
  ],
  "how-to-start-online-eyewear-business-2026": [
    "sell-prescription-glasses-online-without-inventory",
    "building-prescription-eyewear-catalog-without-inventory",
    "optical-lab-networks-backbone-online-eyewear-fulfillment",
  ],
  "sell-prescription-glasses-online-without-inventory": [
    "building-prescription-eyewear-catalog-without-inventory",
    "optical-lab-networks-backbone-online-eyewear-fulfillment",
    "how-to-start-online-eyewear-business-2026",
  ],
};

// Contextual in-body link added to specific posts to surface otherwise-orphaned articles.
const IN_BODY_LINK: Record<string, { slug: string; label: string; intro: string }> = {
  "pupillary-distance-measurement-online": {
    slug: "ai-optical-retail-conversational-ai",
    label: "AI in Optical Retail: How Conversational AI Is Changing Eyewear Sales",
    intro: "Related reading:",
  },
  "five-biggest-mistakes-online-rx-eyewear": {
    slug: "why-most-vto-tools-disappoint",
    label: "Why Most Virtual Try-On Tools Disappoint (And What to Look For)",
    intro: "Related reading:",
  },
};




const BlogPost = () => {
  const { slug } = useParams<{ slug: string }>();
  const loaderData = useLoaderData({ from: "/blog/$slug", structuralSharing: false }) as
    | { post: BlogPost | null }
    | undefined;
  const loadedPost = loaderData?.post ?? null;
  const [post, setPost] = useState<BlogPost | null>(loadedPost);
  const [related, setRelated] = useState<RelatedPost[]>([]);
  const [loading, setLoading] = useState(!loadedPost);

  useEffect(() => {
    // Reset state on slug change so we never render stale data or
    // trigger the not-found redirect while the next fetch is in flight.
    setPost(loadedPost);
    setRelated([]);
    setLoading(!loadedPost);
    let cancelled = false;
    const fetchPost = async () => {
      const data =
        loadedPost ??
        (
          await supabase
            .from("published_cms_posts")
            .select("id, title, slug, excerpt, content, featured_image_url, published_at, updated_at, seo_title, seo_description")
            .eq("slug", slug)
            .eq("status", "published")
            .eq("type", "blog")
            .maybeSingle()
        ).data;
      if (cancelled) return;
      setPost(data);
      setLoading(false);


      if (data) {
        const curated = RELATED_POSTS[data.slug];
        let relQuery = supabase
          .from("published_cms_posts")
          .select("id, title, slug, excerpt, featured_image_url")
          .eq("status", "published")
          .eq("type", "blog")
          .neq("id", data.id);
        if (curated && curated.length) {
          const { data: rel } = await relQuery.in("slug", curated);
          // preserve curated order
          const ordered = curated
            .map((s) => rel?.find((r) => r.slug === s))
            .filter(Boolean) as RelatedPost[];
          setRelated(ordered);
        } else {
          const { data: rel } = await relQuery
            .order("published_at", { ascending: false })
            .limit(3);
          setRelated(rel || []);
        }
      }
    };
    fetchPost();
    return () => {
      cancelled = true;
    };
  }, [slug]);

  // Targeted retired-post redirect (must run before the generic /blog fallback)
  if (slug && RETIRED_POST_REDIRECTS[slug]) {
    return <Navigate to={RETIRED_POST_REDIRECTS[slug]} replace />;
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Navbar />
        <div className="container mx-auto px-4 py-16">
          <div className="mx-auto max-w-3xl space-y-4">
            <div className="h-8 w-3/4 animate-pulse rounded bg-muted" />
            <div className="h-64 animate-pulse rounded-xl bg-muted" />
          </div>
        </div>
      </div>
    );
  }

  if (!post) {
    // Unknown blog post → send users to the blog index (closest topical match)
    return <Navigate to="/blog" replace />;
  }

  const solution = POST_TO_SOLUTION[post.slug];
  const inBody = IN_BODY_LINK[post.slug];

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      <article className="container mx-auto px-4 py-16">
        <div className="mx-auto max-w-3xl">
          <Link
            to="/blog"
            className="mb-8 inline-flex items-center gap-2 text-sm text-muted-foreground transition-colors hover:text-foreground"
          >
            <ArrowLeft size={16} /> Back to Blog
          </Link>

          {post.featured_image_url ? (
            <img
              src={post.featured_image_url}
              alt={post.title}
              width="768"
              height="400"
              className="mb-8 w-full rounded-xl object-cover"
            />
          ) : (
            <div className="mb-8 flex h-64 items-center justify-center rounded-xl bg-muted text-muted-foreground">
              Blog banner placeholder
            </div>
          )}

          {post.published_at && (
            <span className="mb-4 block text-sm text-muted-foreground">
              {format(new Date(post.published_at), "MMMM d, yyyy")}
            </span>
          )}

          <h1 className="mb-8 text-3xl font-extrabold text-foreground md:text-4xl lg:text-5xl">
            {post.title}
          </h1>

          {post.content && (
            <div
              className="prose prose-lg max-w-none text-foreground prose-headings:text-foreground prose-headings:font-bold prose-h2:mt-10 prose-h2:mb-4 prose-h3:mt-8 prose-h3:mb-3 prose-h3:font-semibold prose-p:mb-4 prose-p:leading-relaxed prose-ul:my-4 prose-ul:pl-6 prose-li:mb-2 prose-a:text-primary prose-strong:text-foreground prose-em:text-muted-foreground prose-hr:my-10"
              dangerouslySetInnerHTML={{ __html: sanitizePostHtml(post.content) }}
            />
          )}

          {inBody && (
            <p className="mt-8 text-base italic text-muted-foreground">
              {inBody.intro}{" "}
              <Link to={`/blog/${inBody.slug}`} className="text-primary underline hover:no-underline">
                {inBody.label}
              </Link>
              .
            </p>
          )}

          {/* Contextual solution link */}
          {solution && (
            <aside className="mt-12 rounded-xl border border-border bg-secondary/40 p-6">
              <p className="mb-2 text-sm font-semibold text-foreground">{solution.intro}</p>
              <Link to={solution.href} className="text-base font-medium text-primary hover:underline">
                {solution.label} →
              </Link>
            </aside>
          )}

          <PostFaq faqs={POST_FAQS[post.slug]} />



          {/* Related articles */}
          {related.length > 0 && (
            <section className="mt-16 border-t border-border pt-12" aria-labelledby="related-heading">
              <h2 id="related-heading" className="mb-6 text-2xl font-bold text-foreground">
                Related articles
              </h2>
              <div className="grid gap-6 md:grid-cols-3">
                {related.map((r) => (
                  <Link
                    key={r.id}
                    to={`/blog/${r.slug}`}
                    className="group overflow-hidden rounded-xl border border-border bg-card transition-shadow hover:shadow-lg"
                  >
                    {r.featured_image_url ? (
                      <img
                        src={r.featured_image_url}
                        alt={r.title}
                        loading="lazy"
                        className="h-36 w-full object-cover"
                      />
                    ) : (
                      <div className="flex h-36 items-center justify-center bg-muted text-xs text-muted-foreground">
                        Blog image
                      </div>
                    )}
                    <div className="p-4">
                      <h3 className="mb-1 text-sm font-bold text-card-foreground transition-colors group-hover:text-primary">
                        {r.title}
                      </h3>
                      {r.excerpt && (
                        <p className="line-clamp-2 text-xs text-muted-foreground">{r.excerpt}</p>
                      )}
                    </div>
                  </Link>
                ))}
              </div>
            </section>
          )}
        </div>
      </article>
      <Footer />
    </div>
  );
};

export default BlogPost;
